import React, {FC} from 'react';

import {KeyboardActionsEnum} from '../../../../../../libs/shared/helpers/src/browser/keyboardHelpers';
import {Trans} from '../../../../../../libs/shared/helpers/src/internationalization/react';
import {Renderer} from '../../../../../../libs/shared/helpers/src/react/reactHelpers';
import {ToolbarButton} from '../../../../../../libs/shared/ui/src/components/buttons/toolbarButton';
import {PopoverTransferProps} from '../../../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {UserThemeConsumer} from '../../../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {AbsoluteDateTimeFormatter} from '../../../../../../srcCommon/formatters/dateTime/absoluteDateTimeFormatter';
import {PermissionDenied} from '../../../../../../srcCommon/permissions/permissionDenied';
import {useConversationStatusBulkActionPermission} from '../../../../../../srcCommon/permissions/useBulkActionPermission';
import {useConversationStatusActionOfflinePermission} from '../../../../../../srcCommon/permissions/useOfflinePermission';
import {TimeFormatsEnum} from '../../../../../helpers/dateTimeHelpers';
import {PermissionDeniedReasonsEnum} from '../../../../../helpers/permissionHelpers';
import {ConversationBaseModel} from '../../../../../models/api/conversationModel';
import {ReminderModel} from '../../../../../models/api/reminderModel';
import {PermissionsEnum} from '../../../../../models/api/roleModel';
import {CellsContext} from '../../../../../store/panels/cells/context/cellsContext';
import {InboxesParameters} from '../../../../../store/panels/inboxes/parameters/inboxesParameters';
import {ViewerPanelId} from '../../../../../store/panels/panelStateBase';
import {PermissionPopoverCoordinator} from '../../../../common/popovers/permissionPopoverCoordinator';
import {SnoozeDropdownContainer} from '../actions/snooze/snoozeDropdownContainer';

/*
 * Props.
 */

interface ConversationTopbarSnoozeProps {
  panelId: ViewerPanelId;
  cellsContext: CellsContext;
  conversations: ReadonlyArray<ConversationBaseModel>;
  contextNamespaces: ReadonlyArray<string>;
  reminderForAll: ReminderModel | undefined;
  timeFormat: TimeFormatsEnum;
  isBulkActionRequired: boolean;
  inboxesParameters: InboxesParameters;
  isTooltipMultiline?: boolean;
  renderTooltip?: Renderer;
}

/*
 * Component.
 */

export const ConversationTopbarSnooze: FC<ConversationTopbarSnoozeProps> = (props) => {
  const {
    cellsContext,
    conversations,
    reminderForAll,
    contextNamespaces,
    timeFormat,
    isBulkActionRequired,
    inboxesParameters,
  } = props;
  const offlinePermission = useConversationStatusActionOfflinePermission(cellsContext, conversations);
  const bulkActionPermission = useConversationStatusBulkActionPermission(
    isBulkActionRequired,
    inboxesParameters,
  );

  return (
    <PermissionPopoverCoordinator
      permission={PermissionsEnum.CONVERSATION_ARCHIVE}
      namespaces={contextNamespaces}
      offlinePermission={offlinePermission}
      bulkActionPermission={bulkActionPermission}
      renderButton={(isOpen) => renderButton(props, isOpen, timeFormat, reminderForAll)}
      renderDisabledButton={(reason) => renderDisabledButton(reason)}
      renderModal={(modalProps) => renderDropdown(modalProps, props)}
      keyboardAction={KeyboardActionsEnum.SNOOZE}
    />
  );
};

/*
 * Button.
 */

function renderButton(
  props: ConversationTopbarSnoozeProps,
  isOpen: boolean,
  timeFormat: TimeFormatsEnum,
  reminderForAll?: ReminderModel,
) {
  // If we have no reminder, return the normal icon.
  if (!reminderForAll) {
    return (
      <UserThemeConsumer>
        {(theme) => (
          <ToolbarButton
            iconName="snooze"
            noMargin
            label={<Trans id="toolbar-snooze">Snooze</Trans>}
            keyboardAction={KeyboardActionsEnum.SNOOZE}
            hasHover={isOpen}
            color={theme.greys.shade80}
            hoverColor={theme.greys.shade80}
            isTooltipMultiline={props.isTooltipMultiline}
            renderTooltip={props.renderTooltip}
            placement="bottom"
          />
        )}
      </UserThemeConsumer>
    );
  }

  // Otherwise, render the filled icon.
  return (
    <UserThemeConsumer>
      {(theme) => (
        <ToolbarButton
          iconName="snoozeFilled"
          noMargin
          label={<Trans id="toolbar-snoozed">Snoozed</Trans>}
          color={theme.palette.orange.shade40}
          hoverColor={theme.palette.orange.shade50}
          hasHover={isOpen}
          isTooltipMultiline={props.isTooltipMultiline}
          renderTooltip={() =>
            props.renderTooltip
              ? props.renderTooltip()
              : renderSnoozedUntilTooltip(reminderForAll, timeFormat)
          }
          placement="bottom-end"
        />
      )}
    </UserThemeConsumer>
  );
}

function renderDisabledButton(reason: PermissionDeniedReasonsEnum) {
  return (
    <ToolbarButton
      isDisabled
      noMargin
      iconName="snooze"
      label={<Trans id="toolbar-snooze">Snooze</Trans>}
      renderTooltip={() => <PermissionDenied reason={reason} />}
      placement="bottom-end"
    />
  );
}

function renderSnoozedUntilTooltip(reminder: ReminderModel, timeFormat: TimeFormatsEnum) {
  return (
    <Trans id="toolbar-snoozed-until">
      Snoozed until <AbsoluteDateTimeFormatter value={reminder.remindAt} timeFormat={timeFormat} />
    </Trans>
  );
}

/*
 * Dropdown.
 */

function renderDropdown(modalProps: PopoverTransferProps, props: ConversationTopbarSnoozeProps) {
  return <SnoozeDropdownContainer {...modalProps} {...props} placement="bottom-end" />;
}
